import "https://cdn.jsdelivr.net/npm/@bryt-designs/web-components@0.13.2/dist/jsdelivr/hash-router/index.js";

import StorefrontCart from "https://cdn.jsdelivr.net/npm/@bryt-designs/storefront-cart@2.2.41";
import StorefrontClient from "https://cdn.jsdelivr.net/npm/@bryt-designs/storefront-client@0.7.7";

import {
  createEffect,
  createMemo,
  onCleanup,
  batch
} from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/+esm";
import { createStore } from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/store/+esm";
import { createAjaxCartApi } from "https://cdn.jsdelivr.net/npm/@brytdesigns/shopify-ajax-cart-api@1.0.15/dist/esm/index.min.js";

function createWidgetSettingsUrl(storeUrl) {
  return `https://subs.api.tryprive.com/stores/${storeUrl}/settings`;
}

function createWidgetProductSellingPlansUrl({ storeUrl, productId }) {
  return `https://subs.api.tryprive.com/stores/${storeUrl}/products/${productId}/new`;
}

const client = StorefrontClient({
  shouldPersist: true,
  key: "priveData",
});

async function fetchWidgetSettings(storeUrl, options) {
  const endpoint = createWidgetSettingsUrl(storeUrl);
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    signal: options?.signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function fetchProductSellingPlans({ storeUrl, productId }, options) {
  const endpoint = createWidgetProductSellingPlansUrl({ storeUrl, productId });
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    signal: options?.signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function fetchWidgetData({ signal, queryKey }) {
  const [_, storeUrl, productId] = queryKey;
  if (!productId)
    return {
      settings: null,
      options: null,
    };
  const [settings, options] = await Promise.all([
    fetchWidgetSettings(storeUrl, { signal }),
    fetchProductSellingPlans({ storeUrl, productId }, { signal }),
  ]);

  return { settings, options };
}

const API = createAjaxCartApi({ debug: window.BrytDesigns.debug });

const { useLocation, publishEvent } = window.BundleUtils;

const [_builderStore, setBuilderStore] = createStore({
  selectedProduct: null,
  sellingPlans: [],
  selectedSellingPlan: null,
  currentStep: 0,
  currentPath: "/",
  purchaseOption: 'sub',
  selectedVariant: null,
});


export const updateSelectedProduct = (product) =>
  setBuilderStore("selectedProduct", product);
export const updateCurrentStep = (step) => setBuilderStore("currentStep", step);
export const updateCurrentPath = (path) => setBuilderStore("currentPath", path);
export const updateSellingPlans = (plans) =>
  setBuilderStore("sellingPlans", plans);
export const updateSelectedSellingPlan = (plan) =>
  setBuilderStore("selectedSellingPlan", plan);
export const updatePurchaseOption = (option) =>
  setBuilderStore("purchaseOption", option);
export const updateSelectedVariant = (variant) =>
  setBuilderStore("selectedVariant", variant);

export const builderStore = _builderStore;

const routerElement = document.querySelector("hash-router");
const location = useLocation(routerElement);

const sellingPlansQuery = client.createQuery(() => ({
  queryKey: [
    "priveData",
    "nosh-brands-llc.myshopify.com",
    _builderStore?.selectedProduct?.id,
  ],
  queryFn: fetchWidgetData,
}));

export const sellingPlans = createMemo(() => {
  const data = sellingPlansQuery?.data;
  if (!data) return [];
  const sellingPlans = _builderStore.sellingPlans || [];
  if (!sellingPlans || !sellingPlans?.length) return [];
  const privePlans = data.options?.selling_plan_groups?.flatMap(
    (group) => group.selling_plans,
  );
  return privePlans;
});

createEffect(() => {
  if (StorefrontCart.isLoading) return;
  if (location.pathname !== "/") return;
  async function invoke() {
    await API.clear();
    const options = Array.from(
      document.querySelectorAll('[name="purchaseOption"]'),
    );
    if (!options.length) {
      await API.update({
        attributes: {
          _purchaseOption: "sub",
        },
      });
    }
    await StorefrontCart.refetchCart();
  }
  invoke();
});

createEffect(() => {
  const path = location.pathname;
  const hashRoute = routerElement.querySelector(`hash-route[path="${path}"]`);
  if (!hashRoute) return;
  const title = hashRoute.getAttribute("data-title");
  const sliders = Array.from(hashRoute.querySelectorAll("keen-slider"));
  sliders.forEach((slider) => {
    setTimeout(() => {
      slider?.slider()?.update();
    })
  });
  publishEvent("bundle-builder:step:change", { step: { path, title } });
});

function extractPeriod(input) {
  const lower = input.toLowerCase();
  if (lower.includes("week")) return "WEEK";
  if (lower.includes("month")) return "MONTH";
  if (lower.includes("day") || lower.includes("daily")) return "DAY";
  return "DAY";
}

function getCountByDay(period, unit) {
  if (period === "MONTH") return unit * 28;
  if (period === "WEEK") return unit * 7;
  if (period === "DAY") return unit * 1;
  return unit;
}

export function findVariantByFrequency(freq, unit) {
  const variants = _builderStore.selectedProduct?.variants || [];
  if (!variants.length) return null;
  const period = extractPeriod(unit);
  const days = getCountByDay(period, parseInt(freq));
  const variant = variants.find((variant) => {
    const vPeriod = extractPeriod(variant.title);
    const vUnit = parseInt(variant.title);
    const vDays = getCountByDay(vPeriod, vUnit);
    return days === vDays;
  });
  return variant;
}

createEffect(() => {
  const plans = sellingPlans();
  const selectedProduct = _builderStore.selectedProduct;
  const purchaseOptionBlocks = Array.from(document.querySelectorAll('.bundle-v4-bundle-select'));
  if (!purchaseOptionBlocks.length || !selectedProduct) return;
  const controller = new AbortController();
  purchaseOptionBlocks.forEach((block) => {
    const option = block.querySelector('input[name="purchaseOption"]');
    const button = block.querySelector('button[data-submit]');
    if (!option || !button) return;
    if (option.checked) {
      updatePurchaseOption(option.value);
      button.removeAttribute('disabled')
    } else {
      button.setAttribute('disabled', 'disabled');
    }
    option.addEventListener("change", (e) => {
      batch(() => {
        updatePurchaseOption(option.value);
        updateSelectedSellingPlan(option.getAttribute('data-selling-plan') || null);
        const freq = option.getAttribute('data-frequency'),
          unit = option.getAttribute('data-unit');
        const variant = findVariantByFrequency(freq, unit);
        updateSelectedVariant(variant);
      })
      
      const otherButtons = Array.from(document.querySelectorAll('.bundle-v4-bundle-select button[data-submit]:not([disabled])'));
      otherButtons.forEach((button) => {
        button.setAttribute('disabled', 'disabled');
      })
      button.removeAttribute('disabled');
    }, {
      signal: controller.signal
    })

    button.addEventListener('click', async (e) => {
      e.preventDefault();
      const freq = option.getAttribute('data-frequency');
      const unit = option.getAttribute('data-unit');
      const purchaseOption = option.value;
      if (!freq || !unit) return;
      let variant = null;
      let sellingPlanId = null;
      if (purchaseOption === 'otp') {
        variant = findVariantByFrequency(freq, unit);
      } else {
        sellingPlanId = option.getAttribute('data-selling-plan');
        variant = findVariantByFrequency(freq, unit)
      }
      if (!variant) return;
      try {
        await StorefrontCart.addItem({
          merchandiseId: variant.id,
          quantity: 1,
          sellingPlanId,
        });
        window.location.href = StorefrontCart.cart()?.checkoutUrl;
      } catch {}
    }, {
      signal: controller.signal
    })
  })

  onCleanup(() => {
    controller.abort()
  })
})

createEffect(() => {
  console.log("Purchase option", _builderStore.purchaseOption)
})